import * as Phaser from "phaser"
import { GameState } from "../core/game-state"
import { AUDIO, SCENE, TEX } from "../core/keys"
import { THEME } from "../core/theme"
import { getWaveObserverSettings } from "../core/wave-observer"
import { AUDIO_ASSETS } from "../data/audio"
import { ALL_SECTOR_BACKGROUND_LAYERS } from "../data/backgrounds"
import { HERO_DEFINITIONS } from "../data/characters"
import { CHARACTERS } from "../data/characters"
import { ENEMY_DEFINITIONS } from "../data/enemies"
import {
  type AnimationSpec,
  COIN_ANIMATIONS,
  EXPLOSION_ANIMATIONS,
  HIT_ANIMATIONS,
  PROJECTILE_ANIMATIONS,
} from "../data/fx-animations"
import { ICON_ASSET_DEFINITIONS } from "../data/icon-registry"
import { MISC_ASSETS } from "../data/misc-assets"
import { PortalManager } from "../systems/portal-manager"
import {
  createLoadedAnimations,
  queueBootAssets,
} from "../systems/runtime-assets"
import { initSfxPlayer } from "../systems/sfx-generator"
import { TextureManager } from "../systems/texture-manager"
import { createPlayer, toPublicAssetUrl } from "../utils"

export class BootScene extends Phaser.Scene {
  private textureManager!: TextureManager

  constructor() {
    super({ key: SCENE.BOOT })
  }

  preload(): void {
    this.textureManager = new TextureManager(this)

    this.load.on("loaderror", (file: Phaser.Loader.File) => {
      this.textureManager.handleLoadError({ key: file.key, type: file.type })
    })

    // We no longer call loadGameAssets here to avoid blocking the render
  }

  private loadGameAssets(): void {
    queueBootAssets(this)
    this.loadHeroAssets()
    this.loadEnemyAssets()
    this.loadFxSpritesheets()
    this.loadBackgroundAssets()
    this.loadIconAssets()
    this.loadMiscAssets()
    this.loadAudioAssets()
  }

  private loadHeroAssets(): void {
    for (const hero of HERO_DEFINITIONS) {
      if (hero.animation) {
        this.load.spritesheet(
          hero.textureKey,
          toPublicAssetUrl(hero.animation.assetPath),
          {
            frameWidth: hero.animation.frameWidth,
            frameHeight: hero.animation.frameHeight,
          },
        )
      } else {
        this.load.image(hero.textureKey, toPublicAssetUrl(hero.assetPath))
      }
      this.load.image(
        `portrait_${hero.id}`,
        toPublicAssetUrl(hero.portraitPath),
      )
    }

    // Default player sprite (bella)
    this.load.image(
      TEX.PLAYER_SPRITE,
      toPublicAssetUrl(HERO_DEFINITIONS[0].assetPath),
    )
  }

  private loadEnemyAssets(): void {
    for (const enemy of Object.values(ENEMY_DEFINITIONS)) {
      if (!enemy.texture) continue
      if (this.textures.exists(enemy.texture)) continue
      const isSpriteSheet =
        enemy.assetPath.endsWith("sheet.webp") || !!enemy.animation
      if (isSpriteSheet && enemy.animation) {
        this.load.spritesheet(
          enemy.texture,
          toPublicAssetUrl(enemy.assetPath),
          {
            frameWidth: enemy.animation.frameWidth,
            frameHeight: enemy.animation.frameHeight,
          },
        )
      } else {
        this.load.image(enemy.texture, toPublicAssetUrl(enemy.assetPath))
      }
    }

    this.load.image(
      TEX.ENEMY_MINE_DRONE_CLOSE,
      toPublicAssetUrl("enemies/mine-drone/mine-drone-close.webp"),
    )
    this.load.image(
      TEX.ENEMY_MINE_DRONE_OPEN,
      toPublicAssetUrl("enemies/mine-drone/mine-drone-open.webp"),
    )

    // Exposed brain boss (Neural Commander)
    this.load.spritesheet(
      TEX.BOSS_BRAIN_EXPOSED,
      toPublicAssetUrl("enemies/boss-brain/exposed-idle.webp"),
      {
        frameWidth: 1024,
        frameHeight: 1024,
      },
    )

    // Humanoid boss extra animation sheets (idle is handled via ENEMY_DEFINITIONS)
    const humanoidSheets: [string, string][] = [
      [TEX.BOSS_HUMANOID_JUMP, "enemies/boss-humanoid/jump-sheet.webp"],
      [TEX.BOSS_HUMANOID_DASH, "enemies/boss-humanoid/dash-sheet.webp"],
      [TEX.BOSS_HUMANOID_CHARGE, "enemies/boss-humanoid/charge-sheet.webp"],
      [TEX.BOSS_HUMANOID_FIRE, "enemies/boss-humanoid/fire-sheet.webp"],
    ]
    for (const [key, path] of humanoidSheets) {
      if (!this.textures.exists(key)) {
        this.load.spritesheet(key, toPublicAssetUrl(path), {
          frameWidth: 1024,
          frameHeight: 1024,
        })
      }
    }
  }

  private loadFxSpritesheets(): void {
    const allSpecs: AnimationSpec[] = [
      ...Object.values(EXPLOSION_ANIMATIONS),
      ...Object.values(PROJECTILE_ANIMATIONS),
      ...Object.values(HIT_ANIMATIONS),
      ...Object.values(COIN_ANIMATIONS),
    ]

    for (const spec of allSpecs) {
      this.load.spritesheet(spec.texture, toPublicAssetUrl(spec.assetPath), {
        frameWidth: spec.frameWidth,
        frameHeight: spec.frameHeight,
      })
    }
  }

  private loadBackgroundAssets(): void {
    for (const layer of ALL_SECTOR_BACKGROUND_LAYERS) {
      this.load.image(layer.key, toPublicAssetUrl(layer.assetPath))
    }
  }

  private loadIconAssets(): void {
    for (const icon of ICON_ASSET_DEFINITIONS) {
      this.load.image(icon.textureKey, toPublicAssetUrl(icon.assetPath))
    }
  }

  private loadMiscAssets(): void {
    for (const asset of MISC_ASSETS) {
      this.load.image(asset.textureKey, toPublicAssetUrl(asset.assetPath))
    }
  }

  private loadAudioAssets(): void {
    // Only load UI SFX for instant boot.
    // Heavy music will be loaded when needed by the respective scenes.
    const uiSfx = [AUDIO.SFX_UI_BEEP, AUDIO.SFX_UI_PRESS, AUDIO.SFX_UI_NAVIGATE]
    for (const audio of AUDIO_ASSETS) {
      if (uiSfx.includes(audio.key as any)) {
        this.load.audio(audio.key, toPublicAssetUrl(audio.assetPath))
      }
    }
  }

  create(): void {
    GameState.init(this.game)
    const observer = getWaveObserverSettings()

    this.sound.unlock()
    initSfxPlayer(this)

    const cx = this.cameras.main.width / 2
    const cy = this.cameras.main.height / 2

    // Studio Presentation (Acts as loading screen)
    const studioText = this.add
      .text(cx, cy, "WHO LET THE DOGS OUT?", {
        fontFamily: THEME.fonts.pixel,
        fontSize: "20px",
        color: "#ffffff",
      })
      .setOrigin(0.5)
      .setAlpha(0)

    let assetsLoaded = false
    let introFadeInFinished = false

    const params = new URLSearchParams(window.location.search)
    const isPortal = params.get("portal") === "true"

    const startFadeOut = () => {
      if (isPortal) {
        startNext()
        return
      }
      this.tweens.add({
        targets: studioText,
        alpha: 0,
        duration: 600,
        ease: "Quad.easeIn",
        onComplete: startNext,
      })
    }

    const checkReady = () => {
      if (assetsLoaded && (introFadeInFinished || isPortal)) {
        startFadeOut()
      }
    }

    const startNext = () => {
      this.textureManager.createProceduralTextures()
      createLoadedAnimations(this)
      this.validateTextureRegistry()
      this.createEnemyAnimations()
      this.createHumanoidBossAnimations()
      this.createHeroAnimations()
      this.createFxAnimations()
      this.createCoinAnimations()

      const params = new URLSearchParams(window.location.search)
      const portalManager = PortalManager.instance
      const portalData = portalManager.getPortalData()

      if (portalData.isPortal) {
        const gs = GameState.instance
        gs.reset()

        // Detect hero from params if available, else default to bella
        const heroId = params.get("hero")?.toLowerCase() || "bella"
        const hero =
          HERO_DEFINITIONS.find((h) => h.id === heroId) || HERO_DEFINITIONS[0]

        gs.character = hero
        gs.player = createPlayer(gs.character)
        gs.currentWave = 1
        gs.runState = "playing"

        // If portal data doesn't have custom name/color, use hero defaults
        if (!params.get("username")) {
          gs.playerName = hero.name.toUpperCase()
        }
        if (!params.get("color")) {
          gs.playerColor = hero.glowColor
        }

        portalManager.applyPortalDataToGameState(gs)

        this.scene.start(SCENE.WAVE, { wave: 1, isPortal: true })
        return
      }

      const sceneParam = params.get("scene")

      if (sceneParam) {
        const directScene = this.resolveDirectScene(sceneParam)
        if (directScene) {
          // Read extra test parameters
          const testWave = Number.parseInt(params.get("wave") || "1", 10)
          const testCoins = Number.parseInt(params.get("coins") || "250", 10)
          const testHero = params.get("hero") || ""

          this.startDirectScene(directScene, {
            wave: testWave,
            coins: testCoins,
            heroId: testHero,
          })
          return
        }
      }

      // Launch debug overlay if debug=true is in URL (persists across all scenes)
      if (params.get("debug") === "true" && !observer.enabled) {
        this.scene.launch(SCENE.DEBUG)
      }

      if (observer.enabled) {
        const gs = GameState.instance
        gs.reset()
        gs.character =
          CHARACTERS.find((character) => character.id === observer.heroId) ??
          CHARACTERS[0]
        gs.currentWave = observer.wave
        gs.runState = "playing"
        this.scene.start(SCENE.WAVE, { wave: observer.wave })
        return
      }

      this.scene.start(SCENE.MENU)
    }

    // Start background loading
    this.load.once("complete", () => {
      assetsLoaded = true
      checkReady()
    })
    this.loadGameAssets()
    this.load.start()

    // Intro Sequence
    if (!isPortal) {
      this.tweens.add({
        targets: studioText,
        alpha: 1,
        duration: 800,
        ease: "Quad.easeOut",
        onComplete: () => {
          introFadeInFinished = true
          checkReady()
        },
      })
    } else {
      introFadeInFinished = true
      checkReady()
    }
  }

  private resolveDirectScene(sceneParam: string): string | null {
    const normalized = sceneParam.trim().toLowerCase()

    // Map of lowercase/alias -> actual SCENE key
    const directScenes: Record<string, string> = {
      [SCENE.MENU.toLowerCase()]: SCENE.MENU,
      [SCENE.STORY.toLowerCase()]: SCENE.STORY,
      [SCENE.TUTORIAL.toLowerCase()]: SCENE.TUTORIAL,
      [SCENE.CHARACTER_SELECT.toLowerCase()]: SCENE.CHARACTER_SELECT,
      charselect: SCENE.CHARACTER_SELECT,
      chars: SCENE.CHARACTER_SELECT,
      [SCENE.WAVE.toLowerCase()]: SCENE.WAVE,
      game: SCENE.WAVE,
      play: SCENE.WAVE,
      [SCENE.HUD.toLowerCase()]: SCENE.HUD,
      [SCENE.SHOP.toLowerCase()]: SCENE.SHOP,
      [SCENE.PAUSE.toLowerCase()]: SCENE.PAUSE,
      [SCENE.UPGRADE_CHOICE.toLowerCase()]: SCENE.UPGRADE_CHOICE,
      upgrade: SCENE.UPGRADE_CHOICE,
      levelup: SCENE.UPGRADE_CHOICE,
      [SCENE.GAME_OVER.toLowerCase()]: SCENE.GAME_OVER,
      gameover: SCENE.GAME_OVER,
      death: SCENE.GAME_OVER,
      [SCENE.OPTIONS.toLowerCase()]: SCENE.OPTIONS,
      [SCENE.DEBUG.toLowerCase()]: SCENE.DEBUG,
    }

    return directScenes[normalized] ?? null
  }

  private startDirectScene(
    sceneKey: string,
    options: { wave: number; coins: number; heroId: string },
  ): void {
    const hero =
      CHARACTERS.find(
        (c) => c.id.toLowerCase() === options.heroId.toLowerCase(),
      ) || CHARACTERS[0]
    const player = createPlayer(hero)
    const state = GameState.instance

    state.reset()
    state.character = hero
    state.playerName = hero.name.toUpperCase()
    state.playerColor = hero.glowColor
    state.player = player
    state.currentWave = options.wave
    state.coins = options.coins
    state.score = 0
    state.runState = "menu"

    switch (sceneKey) {
      case SCENE.MENU:
      case SCENE.STORY:
      case SCENE.TUTORIAL:
      case SCENE.CHARACTER_SELECT:
      case SCENE.OPTIONS:
        this.scene.start(sceneKey)
        return
      case SCENE.WAVE:
        state.runState = "playing"
        this.scene.start(SCENE.WAVE, { wave: options.wave })
        return
      case SCENE.HUD:
      case SCENE.SHOP:
      case SCENE.UPGRADE_CHOICE:
      case SCENE.PAUSE:
      case SCENE.DEBUG:
        if (sceneKey === SCENE.SHOP) state.runState = "shop"
        if (sceneKey === SCENE.UPGRADE_CHOICE) state.runState = "upgradeChoice"
        if (sceneKey === SCENE.PAUSE) state.runState = "paused"
        if (sceneKey === SCENE.HUD || sceneKey === SCENE.DEBUG)
          state.runState = "playing"

        this.scene.start(SCENE.WAVE, {
          wave: options.wave,
          isTesting: true,
          testOverlay: sceneKey,
        })
        return
      case SCENE.GAME_OVER:
        state.runState = "gameOver"
        this.scene.start(SCENE.GAME_OVER, {
          isVictory: false,
          score: 0,
          wave: options.wave,
          coins: 0,
          totalBumperHits: 0,
          player,
        })
        return
      default:
        this.scene.start(SCENE.MENU)
    }
  }

  private validateTextureRegistry(): void {
    for (const key of Object.values(TEX)) {
      if (!this.textures.exists(key)) {
        this.textureManager.handleLoadError({ key, type: "image" })
      }
    }

    for (const layer of ALL_SECTOR_BACKGROUND_LAYERS) {
      if (!this.textures.exists(layer.key)) {
        this.textureManager.handleLoadError({ key: layer.key, type: "image" })
      }
    }
  }

  private createEnemyAnimations(): void {
    for (const enemy of Object.values(ENEMY_DEFINITIONS)) {
      if (!enemy.animation) continue
      if (!this.textures.exists(enemy.texture)) continue
      if (this.anims.exists(enemy.animation.key)) {
        this.anims.remove(enemy.animation.key)
      }

      this.anims.create({
        key: enemy.animation.key,
        frames: this.anims.generateFrameNumbers(enemy.texture, {
          start: 0,
          end: enemy.animation.frames - 1,
        }),
        frameRate: enemy.animation.frameRate,
        repeat: enemy.animation.repeat ?? 0,
      })
    }

    // Exposed Brain Phase Animation
    if (
      this.textures.exists(TEX.BOSS_BRAIN_EXPOSED) &&
      !this.anims.exists("anim_brain_exposed")
    ) {
      this.anims.create({
        key: "anim_brain_exposed",
        frames: this.anims.generateFrameNumbers(TEX.BOSS_BRAIN_EXPOSED, {
          start: 0,
          end: 3, // 2x2 = 4 frames
        }),
        frameRate: 8,
        repeat: -1,
      })
    }
  }

  private createHumanoidBossAnimations(): void {
    const specs: Array<{
      key: string
      tex: string
      frames: number
      frameRate: number
    }> = [
      {
        key: "anim_boss_humanoid_jump",
        tex: TEX.BOSS_HUMANOID_JUMP,
        frames: 8,
        frameRate: 8,
      },
      {
        key: "anim_boss_humanoid_dash",
        tex: TEX.BOSS_HUMANOID_DASH,
        frames: 4,
        frameRate: 10,
      },
      {
        key: "anim_boss_humanoid_charge",
        tex: TEX.BOSS_HUMANOID_CHARGE,
        frames: 8,
        frameRate: 8,
      },
      {
        key: "anim_boss_humanoid_fire",
        tex: TEX.BOSS_HUMANOID_FIRE,
        frames: 4,
        frameRate: 12,
      },
    ]
    for (const spec of specs) {
      if (!this.textures.exists(spec.tex)) continue
      if (this.anims.exists(spec.key)) this.anims.remove(spec.key)
      this.anims.create({
        key: spec.key,
        frames: this.anims.generateFrameNumbers(spec.tex, {
          start: 0,
          end: spec.frames - 1,
        }),
        frameRate: spec.frameRate,
        repeat: -1,
      })
    }
  }

  private createHeroAnimations(): void {
    for (const hero of HERO_DEFINITIONS) {
      const animation = hero.animation
      if (!animation) continue
      if (!this.textures.exists(hero.textureKey)) continue
      if (this.anims.exists(animation.key)) {
        this.anims.remove(animation.key)
      }

      this.anims.create({
        key: animation.key,
        frames: this.anims.generateFrameNumbers(hero.textureKey, {
          start: 0,
          end: animation.frames - 1,
        }),
        frameRate: animation.frameRate,
        repeat: animation.repeat ?? -1,
      })
    }
  }

  private createFxAnimations(): void {
    const allSpecs: AnimationSpec[] = [
      ...Object.values(EXPLOSION_ANIMATIONS),
      ...Object.values(PROJECTILE_ANIMATIONS),
      ...Object.values(HIT_ANIMATIONS),
    ]

    for (const cfg of allSpecs) {
      if (!this.textures.exists(cfg.texture) || this.anims.exists(cfg.key))
        continue

      this.anims.create({
        key: cfg.key,
        frames: this.anims.generateFrameNumbers(cfg.texture, {
          start: 0,
          end: cfg.frames - 1,
        }),
        frameRate: cfg.frameRate,
        repeat: cfg.repeat ?? 0,
      })
    }
  }

  private createCoinAnimations(): void {
    for (const cfg of Object.values(COIN_ANIMATIONS)) {
      if (this.anims.exists(cfg.key) || !this.textures.exists(cfg.texture))
        continue

      this.anims.create({
        key: cfg.key,
        frames: this.anims.generateFrameNumbers(cfg.texture, {
          start: 0,
          end: cfg.frames - 1,
        }),
        frameRate: cfg.frameRate,
        repeat: cfg.repeat ?? 0,
      })
    }
  }
}
